import React, { useState, useEffect } from "react";
import "./OneToOneAudioCall.css";
import Vapi from "@vapi-ai/web";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { validateEmailCall } from "../../Redux/Slice/Talk/ValidateEmailCallSlice";
import { Loader } from "../../Common/Loader";
import { ErrorModel } from "../../Model/ErrorModel";
import { SuccessModel } from "../../Model/SuccessModel";
import StartTalk from "./StartTalk";
import TalkBox from "./TalkBox";
import { emailCallWebhook } from "../../Redux/Slice/Talk/EmailCallWebhookSlice";
import FormStep from "./FormStep";

export const vapi = new Vapi(process.env.REACT_APP_VAPI_API_TOKEN);

const Talk = () => {
  const [isMuted, setIsMuted] = useState(vapi.isMuted());
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [step, setStep] = useState(1);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callData, setCallData] = useState(null);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const allParams = location.pathname.split("/");
  const callToken = allParams?.length ? allParams[allParams?.length - 1] : "";
  const assistantId =
    allParams?.length > 1 ? allParams[allParams?.length - 2] : "";
  const isFromForm = location.state?.isFromForm;

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const validateCall = async () => {
    setIsLoading(true);
    const body = { token: callToken };
    const result = await dispatch(validateEmailCall(body));
    if (validateEmailCall.fulfilled.match(result) && result?.payload?.success) {
      setIsLoading(false);
      setCallData(result?.payload?.data);
      if (
        Array.isArray(result?.payload?.data?.form?.fields) &&
        result?.payload?.data?.form?.fields?.length &&
        !isFromForm
      ) {
        setStep(1);
      } else {
        setStep(3);
      }
    } else {
      setIsLoading(false);
      showErrorMessage(
        result?.payload?.error?.message || "Something went wrong.",
        () => navigate("/")
      );
    }
  };

  const startCall = async () => {
    setIsLoading(true);
    try {
      const call = await vapi.start(
        callData?.assistantDetails?.vapiAssistanceId,
        callData?.assistantOverrides ? callData?.assistantOverrides : undefined
      );
      if (call) {
        const payload = {
          assistantId: assistantId,
          token: callToken,
          payload: call,
        };
        dispatch(emailCallWebhook(payload));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMute = () => {
    vapi.setMuted(!isMuted);
    setIsMuted(!isMuted);
  };

  const endCall = () => {
    vapi.stop();
    setIsCallActive(false);
  };

  useEffect(() => {
    validateCall();
    vapi.on("speech-start", () => setIsSpeaking(true));
    vapi.on("speech-end", () => setIsSpeaking(false));
    vapi.on("call-start", () => setIsCallActive(true));
    vapi.on("call-end", () => {
      setIsCallActive(false);
      showSuccessMessage("Chat ended successfully!", () => navigate("/"));
    });

    return () => {
      vapi.stop();
    };
  }, []);

  return (
    <>
      {isLoading && <Loader />}
      {errorModal.visible && <ErrorModel message={errorModal.message} />}
      {successModal.visible && <SuccessModel message={successModal.message} />}
      {step != 3 ? (
        <FormStep
          step={step}
          setStep={setStep}
          setIsLoading={setIsLoading}
          showErrorMessage={showErrorMessage}
          showSuccessMessage={showSuccessMessage}
          callData={callData}
          setCallData={setCallData}
        />
      ) : !isCallActive ? (
        <StartTalk callData={callData} onPressStart={startCall} />
      ) : (
        <TalkBox
          isSpeaking={isSpeaking}
          onEndCall={endCall}
          callData={callData}
        />
      )}
    </>
  );
};

export default Talk;
